import type { intelligence } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { formatMeasure } from './format.js';
import { recordLink } from './recordRoutes.js';

/**
 * One table cell, rendered from its typed arm.
 *
 * The union is the whole point. A cell that arrived as a string could not tell
 * a zero from a withheld figure from a value nobody computed, and there would
 * be nothing stopping a uuid from reaching the screen. Here each arm renders
 * the only thing it can honestly render:
 *
 *   - `number`  — the value at the precision the payload declared.
 *   - `withheld`— the WORD, never a digit. Small-N suppression is a real figure
 *                 kept from this caller, and a `0` in its place is the product's
 *                 worst defect wearing the answer's typeface.
 *   - `absent`  — an em dash. Visibly not a number, and a different fact from
 *                 withheld: the server answered and had nothing here.
 *   - `record`  — the record's LABEL, linked. The id addresses the route and
 *                 never appears in the text; the payload requires a label, so a
 *                 uuid has no path to the screen even for a record whose name
 *                 the caller may not see (such a record simply is not sent).
 */
export function ResultCellView({ cell }: { cell: intelligence.ResultCell | undefined }) {
  // A column the row does not carry. Not a zero, not a blank: the same em dash
  // as an explicit absence, because that is exactly what it is.
  if (cell === undefined) return <span className="rn-muted">—</span>;

  switch (cell.kind) {
    case 'text':
      return <>{cell.text}</>;
    case 'number':
      return <>{formatMeasure(cell.value, cell.unit ?? 'count', cell.precision)}</>;
    case 'status':
      return <Pill variant={cell.tone}>{cell.label}</Pill>;
    case 'record':
      return <RecordCellLink record={cell.record} />;
    case 'withheld':
      return (
        <span className="rn-muted" style={{ fontStyle: 'italic' }}>
          Withheld
        </span>
      );
    case 'absent':
      return <span className="rn-muted">—</span>;
  }
}

/**
 * A record inside a cell: the label, linked when this app can address the
 * record itself.
 *
 * A SURFACE-precision destination is deliberately NOT linked from a cell. In a
 * card there is room to say "Open in Scheduling" and mean it; in a table cell
 * the only text is the record's name, so linking it to a list would promise
 * that clicking a shift's name opens that shift. It does not, so it is plain
 * text and the card is where the surface link lives.
 */
function RecordCellLink({ record }: { record: intelligence.ResultRecord }) {
  const link = recordLink(record);
  if (link === null || link.precision !== 'record') return <>{record.label}</>;
  return (
    <a href={link.href} className="rn-link">
      {record.label}
    </a>
  );
}
